import {
  SVG,
  TierInfo,
  UserTierTypes,
  formatNumber,
  useIsDesktop,
  useIsLG,
  useIsWeb,
  ProgressBar,
} from '@eCommerce/frontend-legacy-shared-library-native';
import BottomRightWaves from 'assets/wavesBottomRight.svg';
import {Text, View} from 'native-base';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Animated, Platform} from 'react-native';
import {AnimatedCircularProgress} from 'react-native-circular-progress';

import Divider from './Divider';
import NextTierCredits from './NextTierCredits/NextTierCredits';
import UserInfoSummary from './UserInfoSummary';
import {useTheme} from 'styled-components/native';
import SEVEN_STARS_PATTERN from 'assets/sevenstars_pattern.svg';
import CurrentTierCredits from './CurrentTierCredits';
import SevenStarsInnerCircle from './SevenStarsInnerCircle/SevenStarsInnerCircle';
import InnerCircle from './InnerCircle';

export interface RewardsSummaryModuleProps {
  rewardCredits: number;
  tierCredits: number;
  currentLevel?: UserTierTypes; // Optional to handle loading state before API returns data
  currentTierInfo?: TierInfo; // Optional to handle loading state before API returns data
  nextTierInfo?: TierInfo; // Optional to handle loading state before API returns data
  isGiftedTier?: boolean;
  fullName: string;
  accountId: string;
  asOfDate: string;
  expirationDate: string;
  lastEarnedDate: string;
}

const RewardsSummaryModule: React.FC<RewardsSummaryModuleProps> = props => {
  const {
    rewardCredits,
    tierCredits,
    currentLevel,
    currentTierInfo,
    nextTierInfo,
    fullName,
    accountId,
    isGiftedTier = false,
    asOfDate,
    expirationDate,
    lastEarnedDate,
  } = props;

  const [isOpen, setIsOpen] = useState(true);

  const isLG = useIsLG();
  const isDesktop = useIsDesktop();
  const isWeb = useIsWeb();
  const theme = useTheme() as any;
  const {
    paleSky,
    shuttleGray,
    athens,
    white,
    addAlpha,
    richBlack,
    tuna,
    tierColors,
  } = theme.colors;

  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Fade in/out background waves when user expands/collapses the module
  const fadeWavesIn = () => {
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  };

  const fadeWavesOut = () => {
    Animated.timing(fadeAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: Platform.OS !== 'web',
    }).start();
  };

  useEffect(() => {
    if (isOpen) {
      fadeWavesIn();
    }

    if (!isOpen) {
      fadeWavesOut();
    }
  }, [isOpen]);

  const isSevenStars = currentLevel === UserTierTypes.SEVEN_STARS;
  const isPlatinum = currentLevel === UserTierTypes.PLATINUM;
  const currentTierCredits = tierCredits;
  const sevenStarsExperienceCreditCost = 250000;

  const percentage = useMemo(() => {
    if (!currentTierInfo || !nextTierInfo) {
      return 0;
    }

    const minCredits = currentTierInfo?.mincredits || 0;
    const maxCredits = currentTierInfo?.maxcredits || 0;

    if (isPlatinum) {
      return (currentTierCredits / nextTierInfo.mincredits) * 100;
    }

    // Seven Stars shows progression toward Experience Credits (250k increments)
    if (isSevenStars) {
      // If user has more than one experience credit, show progress to next one
      if (currentTierCredits > sevenStarsExperienceCreditCost) {
        return (
          ((currentTierCredits % sevenStarsExperienceCreditCost) /
            sevenStarsExperienceCreditCost) *
          100
        );
      }

      return (currentTierCredits / sevenStarsExperienceCreditCost) * 100;
    }

    return Math.max(0.0, (currentTierCredits / maxCredits) * 100);
  }, [
    currentTierCredits,
    currentTierInfo,
    nextTierInfo,
    isSevenStars,
    isPlatinum,
  ]);

  // Render placeholder to maintain layout while tier data loads
  if (!currentLevel || !currentTierInfo || !nextTierInfo) {
    return (
      <View
        height={isDesktop ? '525px' : undefined}
        width={isDesktop ? '343px' : '100%'}
        backgroundColor={white}
        shadow={0}
        padding={isDesktop ? '48px' : '24px'}
        mr={isDesktop ? '48px' : undefined}
        overflow="hidden"
      />
    );
  }

  const remainingCreditForNextLevel = isSevenStars
    ? // is the user SevenStars tier - show progression to next Experience Credit
      currentTierCredits > sevenStarsExperienceCreditCost
      ? // subtract the remainder of tierCredits/250k from 250k to get the accurate amount needed for next Experience Credit
        sevenStarsExperienceCreditCost -
        (currentTierCredits % sevenStarsExperienceCreditCost)
      : // subtract the tierCredits from the Experience Credit amount
        sevenStarsExperienceCreditCost - currentTierCredits
    : // for all other tiers, show progression to next tier
      (nextTierInfo?.mincredits || 0) - currentTierCredits;

  const maxValueBeforeOverflow = 999999999;
  const rewardCreditsFormatted = rewardCredits
    ? rewardCredits > maxValueBeforeOverflow
      ? formatNumber(maxValueBeforeOverflow)
      : formatNumber(rewardCredits)
    : 0;

  const trackColor = tierColors[currentLevel]?.track || '#BEC0C5';

  const getContainerHeight = () => {
    if (!isSevenStars && rewardCredits < 0) {
      return '300px';
    }

    if (isSevenStars) {
      if (isLG) {
        if (lastEarnedDate && expirationDate) {
          return '810px';
        }
        return '740px';
      }
      return '690px';
    }

    if (isLG) {
      return '622px';
    }
    return isWeb ? '525px' : '500px';
  };

  return (
    <View
      height={isDesktop ? getContainerHeight() : undefined}
      width={isDesktop ? '343px' : '100%'}
      backgroundColor={white}
      shadow={0}
      padding={isDesktop ? '48px' : '24px'}
      mr={isDesktop ? '48px' : undefined}
      overflow="hidden">
      {isDesktop && (
        <Animated.View
          style={{
            transform: [{scaleX: -1}],
            position: 'absolute',
            bottom: 0,
            right: isWeb ? -18 : isDesktop ? -48 : -24,
            width: 300,
            opacity: fadeAnim,
          }}>
          <SVG source={BottomRightWaves} width={300} height={200} />
        </Animated.View>
      )}
      <View
        flexDirection={isDesktop ? 'column' : 'row'}
        justifyContent={isDesktop ? undefined : 'space-between'}>
        <UserInfoSummary
          fullName={fullName}
          accountId={accountId}
          currentLevel={currentLevel}
          toggleExpand={() => setIsOpen(!isOpen)}
          isOpen={isOpen}
          isGiftedTier={isGiftedTier}
        />
        {isDesktop || rewardCredits < 0 ? null : (
          <View alignItems="flex-end" justifyContent="flex-end">
            <Text
              fontFamily="heading"
              fontWeight="bold"
              fontSize="18px"
              paddingBottom="8px"
              numberOfLines={1}
              color={tuna}>
              {rewardCreditsFormatted}
            </Text>
            <Text
              fontFamily="body"
              fontWeight="bold"
              fontSize="9px"
              letterSpacing="1px"
              color={paleSky}
              textTransform="uppercase">
              reward credits
            </Text>
          </View>
        )}
      </View>

      {rewardCredits < 0 ? (
        <View paddingTop={isDesktop ? '48px' : '24px'} alignItems="center">
          <Text
            fontFamily="body"
            fontWeight="bold"
            fontSize="12px"
            letterSpacing="1px"
            color={paleSky}
            textTransform="uppercase">
            Error, credits not available
          </Text>
        </View>
      ) : isDesktop ? (
        <View>
          <Divider />
          <View paddingTop="8px">
            <View alignItems="center">
              <Text
                fontFamily="heading"
                fontWeight="bold"
                fontSize="40px"
                lineHeight="40px"
                paddingBottom="8px"
                color={shuttleGray}>
                {rewardCreditsFormatted}
              </Text>
              <Text
                fontFamily="body"
                fontWeight="bold"
                fontSize="9px"
                letterSpacing="1px"
                color={paleSky}
                textTransform="uppercase">
                reward credits
              </Text>
            </View>
            <View marginTop="24px" marginBottom="24px" alignItems="center">
              {isSevenStars && (
                <View borderRadius="8px" overflow="hidden">
                  <SVG source={SEVEN_STARS_PATTERN} height={388} width={248} />
                </View>
              )}
              <View
                position={isWeb && !isSevenStars ? 'relative' : 'absolute'}
                alignItems="center">
                <View
                  mb="24px"
                  alignItems="center"
                  pt={isSevenStars ? '32px' : undefined}
                  paddingX={isSevenStars ? '48px' : '76px'}>
                  <AnimatedCircularProgress
                    size={142}
                    width={16}
                    fill={percentage}
                    tintColor={trackColor}
                    onAnimationComplete={() => {}}
                    backgroundColor={
                      isSevenStars ? addAlpha(richBlack, 0.4) : athens
                    }
                    fillLineCap="round" //subject to change if they want no track at 0%
                    rotation={360}>
                    {() =>
                      isSevenStars ? (
                        <SevenStarsInnerCircle
                          tierCredits={currentTierCredits}
                        />
                      ) : (
                        <InnerCircle tierCredits={currentTierCredits} />
                      )
                    }
                  </AnimatedCircularProgress>
                </View>
                <NextTierCredits
                  currentLevel={currentLevel}
                  remainingCreditForNextLevel={remainingCreditForNextLevel}
                  nextTier={nextTierInfo?.name}
                  isGiftedTier={isGiftedTier}
                />
                {lastEarnedDate && expirationDate ? (
                  <View
                    paddingTop={isSevenStars ? '96px' : '24px'}
                    alignItems="center">
                    <View flexDirection={'row'}>
                      <Text
                        fontFamily="body"
                        fontSize={'14px'}
                        color={shuttleGray}>
                        Last credits earned:{' '}
                        <Text fontWeight={600}>{lastEarnedDate}</Text>
                      </Text>
                    </View>
                    <Text
                      paddingTop={'12px'}
                      fontFamily="body"
                      fontSize={'14px'}
                      color={shuttleGray}
                      textAlign="center">
                      Earn more Reward Credits before{' '}
                      <Text fontWeight={600} fontSize={'14px'}>
                        {expirationDate}
                      </Text>{' '}
                      to retain your balance
                    </Text>
                  </View>
                ) : null}
              </View>
            </View>
          </View>
        </View>
      ) : (
        <>
          <View paddingTop="12px">
            <>
              <ProgressBar
                color={trackColor}
                size={10}
                percentage={percentage / 100}
              />

              <View
                justifyContent="space-between"
                flexDirection="row"
                marginTop="4px">
                <CurrentTierCredits tierCredits={currentTierCredits} />
                <NextTierCredits
                  currentLevel={currentLevel}
                  remainingCreditForNextLevel={remainingCreditForNextLevel}
                  nextTier={nextTierInfo?.name}
                  isGiftedTier={isGiftedTier}
                />
              </View>
            </>
          </View>
          {lastEarnedDate && expirationDate ? (
            <>
              <Text
                paddingTop={'8px'}
                fontFamily="body"
                fontSize={'11px'}
                color={shuttleGray}>
                Last credits earned:{' '}
                <Text fontWeight={600}>{lastEarnedDate}</Text>
              </Text>
              <Text
                paddingTop={'8px'}
                fontFamily="body"
                fontSize={'11px'}
                color={shuttleGray}>
                Earn more Reward Credits before{' '}
                <Text fontWeight={600}>{expirationDate}</Text> to retain your
                balance
              </Text>
            </>
          ) : null}
        </>
      )}
    </View>
  );
};

export default RewardsSummaryModule;
